import { LicensePlateValidator } from '../validators/license-plate.validator';

/** When the user does not prefix with a real US state code (e.g. `8AUG441`), APIs still need a state. */
export const DEFAULT_LICENSE_PLATE_STATE = 'CA';

export interface SplitLicensePlateForApiResult {
  state: string;
  /** Characters after an explicit state prefix, or the full normalized plate when no prefix applies */
  license_plate: string;
}

function isTwoLetterUsState(code: string): boolean {
  return /^[A-Z]{2}$/.test(code) && LicensePlateValidator.USA_STATE_CODES.includes(code);
}

/** Returns a valid 2-letter US state code from a hint, or null */
export function coerceUsStateCode(s: string | null | undefined): string | null {
  const t = (s || '').trim().toUpperCase();
  return isTwoLetterUsState(t) ? t : null;
}

function trySplitLeadingState(value: string): SplitLicensePlateForApiResult | null {
  const prefix = value.slice(0, 2);
  if (value.length > 2 && isTwoLetterUsState(prefix)) {
    return { state: prefix, license_plate: value.slice(2) };
  }
  return null;
}

/**
 * Split license input for vehicle APIs.
 *
 * When `hintState` is a valid US code (notification payloads carry `state` and `plate`
 * separately), that state is authoritative: the plate value is used as-is except when
 * it still includes the same state prefix (e.g. `CA` + `CA8AUG441` → `8AUG441`).
 *
 * Without a hint, legacy combined input is parsed:
 * - `CA8AUG441` → state `CA`, plate `8AUG441`
 * - `AZRD34373` → state `AZ`, plate `RD34373`
 * - `8AUG441` → state {@link DEFAULT_LICENSE_PLATE_STATE}, plate `8AUG441`
 */
export function splitLicensePlateForApi(
  fullPlate: string,
  hintState?: string | null
): SplitLicensePlateForApiResult {
  const raw = (fullPlate || '').trim().toUpperCase().replace(/[\s-]/g, '');
  const fallbackState = coerceUsStateCode(hintState) ?? DEFAULT_LICENSE_PLATE_STATE;
  const hint = coerceUsStateCode(hintState);

  if (!raw) {
    return { state: fallbackState, license_plate: '' };
  }

  if (hint) {
    if (raw.length > hint.length && raw.startsWith(hint)) {
      return { state: hint, license_plate: raw.slice(hint.length) };
    }
    return { state: hint, license_plate: raw };
  }

  const direct = trySplitLeadingState(raw);
  if (direct) {
    return direct;
  }

  return { state: fallbackState, license_plate: raw };
}
